import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  SaveOutlined,
  CancelOutlined,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import { useFactureStore } from '../stores/useFactureStore';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useClientStore } from '../stores/useClientStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useNotification } from '../contexts/NotificationContext';
import { TypeFacture, StatutFacture, Facture, TypeOperation, MoyenPaiement } from '../types';
import { formatEuro } from '../utils/calculations';

type FactureFormData = Omit<
  Facture,
  'id' | 'numero' | 'createdAt' | 'updatedAt' | 'montantHT' | 'montantTVA' | 'montantTTC' | 'montantAcompte'
>;

const FactureFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { devisId } = useParams<{ devisId: string }>();

  const { creerFactureAcompte, creerFactureSolde, getFacturesByDevisId } = useFactureStore();
  const { getDevisById } = useQuoteStore();
  const { getClientById } = useClientStore();
  const { user } = useAuthStore();
  const { showError } = useNotification();

  const [devis, setDevis] = useState(devisId ? getDevisById(parseInt(devisId, 10)) : null);
  const [totaux, setTotaux] = useState({ montantHT: 0, montantTVA: 0, montantTTC: 0, montantAcompte: 0 });
  const [facturesExistantes, setFacturesExistantes] = useState<any[]>([]);
  const [acompteExiste, setAcompteExiste] = useState(false);

  // Charger les factures existantes au montage du composant
  useEffect(() => {
    if (devisId) {
      const factures = getFacturesByDevisId(parseInt(devisId, 10));
      setFacturesExistantes(factures);
      setAcompteExiste(factures.some(f => f.type === TypeFacture.ACOMPTE));

      // Vérifier si le devis est déjà complètement facturé
      const devisCompletementFacture = factures.some(
        f => f.type === TypeFacture.COMPLETE || f.type === TypeFacture.SOLDE
      );

      if (devisCompletementFacture) {
        showError('Ce devis a déjà été complètement facturé. Vous ne pouvez plus créer de facture pour ce devis.');
        navigate(`/devis/${devisId}`);
      }
    }
  }, [devisId, getFacturesByDevisId, navigate]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FactureFormData>({
    defaultValues: {
      devisId: devisId ? parseInt(devisId, 10) : 0,
      clientId: devis?.clientId || 0,
      dateEmission: new Date().toISOString().split('T')[0],
      dateEcheance: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      statut: StatutFacture.EN_ATTENTE,
      type: acompteExiste ? TypeFacture.SOLDE : TypeFacture.ACOMPTE,
      pourcentageAcompte: 30,
      tauxTVA: devis?.tauxTVA !== undefined ? devis.tauxTVA : 20, // Fix: gérer le cas où tauxTVA = 0
      notes: acompteExiste ? 'Facture de solde - Montant restant après acompte' : 'Facture d\'acompte - 30% du montant total',
      moyenPaiement: MoyenPaiement.VIREMENT,
      // Champs Factur-X
      typeOperation: TypeOperation.SERVICE,
      devise: 'EUR',
    },
  });

  const pourcentageAcompte = watch('pourcentageAcompte');
  const tauxTVA = watch('tauxTVA');
  const type = watch('type');

  useEffect(() => {
    if (devisId && devis) {
      // Calculer les montants en fonction du type et du pourcentage
      let montantHT = 0;

      if (type === TypeFacture.ACOMPTE && pourcentageAcompte) {
        montantHT = ((devis.montantHT || devis.sousTotal) * pourcentageAcompte) / 100;
      } else if (type === TypeFacture.COMPLETE) {
        montantHT = devis.montantHT || devis.sousTotal;
      } else if (type === TypeFacture.SOLDE) {
        // Calculer le montant du solde = Total - somme des acomptes
        const totalDevis = devis.montantHT || devis.sousTotal;
        const totalAcomptes = facturesExistantes
          .filter(f => f.type === TypeFacture.ACOMPTE)
          .reduce((sum, f) => sum + (f.montantHT || 0), 0);
        montantHT = totalDevis - totalAcomptes;
      }

      const montantTVA = (montantHT * tauxTVA) / 100;
      const montantTTC = montantHT + montantTVA;
      const montantAcompte = montantTTC;

      setTotaux({
        montantHT: Math.round(montantHT * 100) / 100,
        montantTVA: Math.round(montantTVA * 100) / 100,
        montantTTC: Math.round(montantTTC * 100) / 100,
        montantAcompte: Math.round(montantAcompte * 100) / 100,
      });
    }
  }, [devis, type, pourcentageAcompte, tauxTVA, devisId]);

  if (!devis || !user) {
    return (
      <Box>
        <Alert severity="error">Devis non trouvé ou utilisateur non authentifié</Alert>
        <Button onClick={() => navigate('/devis')} sx={{ mt: 2 }}>
          Retour aux devis
        </Button>
      </Box>
    );
  }

  const client = getClientById(devis.clientId);

  if (!client) {
    return (
      <Box>
        <Alert severity="error">Client non trouvé</Alert>
        <Button onClick={() => navigate('/devis')} sx={{ mt: 2 }}>
          Retour aux devis
        </Button>
      </Box>
    );
  }

  const onSubmit = async (data: FactureFormData) => {
    try {
      if (type === TypeFacture.ACOMPTE) {
        // Créer une facture d'acompte
        const percentage = data.pourcentageAcompte || 30;
        await creerFactureAcompte(parseInt(devisId!), percentage);
      } else if (type === TypeFacture.SOLDE) {
        // Créer une facture de solde
        if (!devis.projetId) {
          showError('Impossible de créer une facture de solde : le devis n\'est pas associé à un projet');
          return;
        }
        await creerFactureSolde(devis.projetId, parseInt(devisId!));
      } else {
        // TypeFacture.COMPLETE : traiter comme un solde à 100%
        if (!devis.projetId) {
          showError('Impossible de créer la facture : le devis n\'est pas associé à un projet');
          return;
        }
        await creerFactureSolde(devis.projetId, parseInt(devisId!));
      }
      navigate(`/devis/${devisId}`);
    } catch (error) {
      console.error('Erreur lors de la création de la facture:', error);
      showError('Erreur lors de la création de la facture. Vérifiez la console pour plus de détails.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#333', mb: 3 }}>
        Créer une facture pour le devis {devis.numero}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Informations du devis */}
        <Paper sx={{ p: 4, mb: 3, boxShadow: 2, borderRadius: 2, bgcolor: '#F9FAFB' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
            Informations du devis
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Client: <strong>{client.prenom} {client.nom}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Montant total du devis: <strong>{formatEuro(devis.totalTTC)}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Informations de la facture */}
        <Paper sx={{ p: 4, mb: 3, boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, color: '#374151' }}>
            Informations de la facture
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Type de facture</InputLabel>
                    <Select
                      {...field}
                      label="Type de facture"
                      sx={{ '& .MuiSelect-select': { py: 2 } }}
                      onChange={(e) => {
                        field.onChange(e);
                        // Mettre à jour les notes selon le type
                        if (e.target.value === TypeFacture.ACOMPTE) {
                          setValue('notes', 'Facture d\'acompte - 30% du montant total');
                        } else if (e.target.value === TypeFacture.SOLDE) {
                          setValue('notes', 'Facture de solde - Montant restant après acompte');
                        } else {
                          setValue('notes', 'Facture complète - Montant total du devis');
                        }
                      }}
                    >
                      <MenuItem value={TypeFacture.ACOMPTE}>💰 Facture d'acompte</MenuItem>
                      {acompteExiste ? (
                        <MenuItem value={TypeFacture.SOLDE}>💵 Facture de solde</MenuItem>
                      ) : (
                        <MenuItem value={TypeFacture.COMPLETE}>📄 Facture complète</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {type === TypeFacture.ACOMPTE && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="pourcentageAcompte"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Pourcentage d'acompte"
                      type="number"
                      inputProps={{ min: 1, max: 100, step: 1 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller
                name="dateEmission"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date d'émission"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="dateEcheance"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date d'échéance"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="statut"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Statut</InputLabel>
                    <Select {...field} label="Statut" sx={{ '& .MuiSelect-select': { py: 2 } }}>
                      <MenuItem value={StatutFacture.EN_ATTENTE}>⏳ En attente</MenuItem>
                      <MenuItem value={StatutFacture.PAYEE}>✅ Payée</MenuItem>
                      <MenuItem value={StatutFacture.ANNULEE}>❌ Annulée</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="moyenPaiement"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Moyen de paiement</InputLabel>
                    <Select {...field} label="Moyen de paiement" sx={{ '& .MuiSelect-select': { py: 2 } }}>
                      <MenuItem value={MoyenPaiement.VIREMENT}>🏦 Virement</MenuItem>
                      <MenuItem value={MoyenPaiement.CB}>💳 Carte bancaire</MenuItem>
                      <MenuItem value={MoyenPaiement.ESPECES}>💵 Espèces</MenuItem>
                      <MenuItem value={MoyenPaiement.CHEQUE}>📝 Chèque</MenuItem>
                      <MenuItem value={MoyenPaiement.AUTRE}>➕ Autre</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Champs Factur-X (conformité e-invoicing 2026) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: '#374151', fontWeight: 600 }}>
                Informations Factur-X
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="typeOperation"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ backgroundColor: 'white', px: 0.5 }}>Type d'opération</InputLabel>
                    <Select {...field} label="Type d'opération" sx={{ '& .MuiSelect-select': { py: 2 } }}>
                      <MenuItem value={TypeOperation.SERVICE}>🔧 Prestation de service</MenuItem>
                      <MenuItem value={TypeOperation.VENTE}>📦 Vente de marchandises</MenuItem>
                      <MenuItem value={TypeOperation.MIXTE}>🔀 Service + Vente (Mixte)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="devise"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Devise (ISO)"
                    placeholder="EUR"
                    helperText="Code devise ISO (ex: EUR, USD)"
                    sx={{ mb: 2 }}
                    inputProps={{
                      maxLength: 3,
                      style: { textTransform: 'uppercase' },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Totaux */}
        <Paper sx={{ p: 4, mb: 3, bgcolor: '#F9FAFB', boxShadow: 2, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
            💰 Totaux de la facture
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body1">Montant HT:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatEuro(totaux.montantHT)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body1">TVA ({tauxTVA}%):</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatEuro(totaux.montantTVA)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 2,
                  borderTop: '2px solid #D1D5DB',
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  TOTAL TTC:
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#10B981' }}>
                  {formatEuro(totaux.montantTTC)}
                </Typography>
              </Box>
            </Grid>
            {type === TypeFacture.ACOMPTE && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Cette facture représente {pourcentageAcompte}% du montant total du devis ({formatEuro(devis.totalTTC)})
                </Alert>
              </Grid>
            )}
            {type === TypeFacture.SOLDE && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mt: 2 }}>
                  Cette facture de solde représente le montant restant après déduction des acomptes déjà versés.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Boutons d'action */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<SaveOutlined />}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              bgcolor: '#F59E0B',
              '&:hover': { bgcolor: '#D97706' },
            }}
          >
            Créer la facture
          </Button>

          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<CancelOutlined />}
            onClick={() => navigate(`/devis/${devisId}`)}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            Annuler
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FactureFormPage;
